<template>
  <ElConfigProvider :z-index="3000">
    <div>
      <router-view />
      <ConfirmDialog />
    </div>
  </ElConfigProvider>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from "vue";
import { attachedVueElement } from "@/utils/helpers/scanner";
import { useI18n } from "vue-i18n";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { LogService } from "./services/logging";
import BookingModule from "./store/modules/booking";
import ReceiptModule from "./store/modules/receipt";
import { monitorReceiptSynchingIssues } from "./utils/vue-helpers/receipt-counter";
import * as Sentry from "@sentry/vue";
import AppModule from "./store/modules/app";
import { useIdle } from "@vueuse/core";
import {
  registerUpdate,
  autoUpdate,
  applyPendingUpdate,
  canRefresh,
  checkForUpdate,
  getTotalMemoryUsed,
  monitorAppPerformance,
} from "@/utils/vue-helpers/common";
import { loadStatusPage, loadStonly } from "./utils/vue-helpers/scripts";
import {
  logPendingIgnoredAndFailedBookings,
  resetGrowingCollecitonsFromUI,
  resetGrowingCollections,
} from "./utils/vue-helpers/db";
import { ModalStatus } from "./constants/modal";
import ModalModule from "./store/modules/modal";
import { syncLogs } from "./db/poslogs";
import { cashPointClosuresSynchHandler } from "./db/cashpoint-closure";
import { loadOffers } from "@/utils/helpers/offers";
import { checkUserAccessToken } from "@/utils/helpers/auth";
import { MEMORY_USAGE_THRESHOLD } from "./utils/constants";
import { isCustomerDisplayWindow } from "@/utils/setup";
import {
  isBelgianCompliant,
  isFrenchCompliant,
  isSpanishCompliant,
} from "./utils/vue-helpers/compliance";
import {
  initializeComplianceLib,
  testEfrConnection,
  testFdmConnection,
} from "./utils/helpers/compliance";
import ComplianceModule from "./store/modules/compliance";
import ConfirmDialog from "@/components/modal/ConfirmDialog.vue";
import { ElConfigProvider } from "element-plus";

export default defineComponent({
  name: "app",
  components: { ConfirmDialog, ElConfigProvider },
  setup() {
    const bookingUuid = computed(() => {
      return BookingModule.selectedBookingUUID;
    });

    // Skip main window watchers/listeners for customer display
    if (!isCustomerDisplayWindow()) {
      // if the app has been idle for 1 min then we check whether we should update/refresh the app.
      const { idle } = useIdle(1 * 60 * 1000);
      // Apply a pending update once the terminal is idle
      watch(
        () => idle.value && AppModule.isUpdateAvailable,
        (shouldUpdate) => {
          if (!shouldUpdate) return;
          LogService.info("Auto-update triggered due to idle time");
          autoUpdate();
        }
      );
      watch(idle, (isIdle) => {
        if (!isIdle || AppModule.isUpdateAvailable) return;
        const totalMemoryUsed = getTotalMemoryUsed();
        if (
          totalMemoryUsed > MEMORY_USAGE_THRESHOLD &&
          !AppModule.isRefreshing &&
          canRefresh()
        ) {
          LogService.info(
            `Memory consumption reached ${totalMemoryUsed} MB, going to refresh the page`
          );
          AppModule.setIsRefreshing(true);
          window.location.reload();
        }
      });

      // Set the bookingUuid as an extra tag for Sentry
      watch(
        bookingUuid,
        () => {
          Sentry.setTags({
            bookingUuid: bookingUuid.value,
          });
        },
        { deep: true }
      );

      // Apply a pending update immediately.
      document.addEventListener("swUpdated", registerUpdate);
      applyPendingUpdate();
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
      window.addEventListener("focus", () => checkForUpdate());
    }

    const booking = computed(() => {
      return BookingModule.selectedBooking;
    });

    const { t } = useI18n({});

    async function refreshWarning(event: Event) {
      if (ModalModule.status === ModalStatus.PaymentProgress) {
        event.preventDefault();
        event.returnValue = true; // legacy support
        if (!window.electronAPI) {
          return;
        }
        LogService.info(
          "User tried to refresh during a card payment, prompting for confirmation"
        );
        const confirmed = await showConfirm({
          title: t("common.warning"),
          message: t("warning.page-refresh"),
          variant: "warning",
        });

        if (confirmed) {
          LogService.info(`User confirmed refreshing during card payment`);
          ModalModule.setStatus(ModalStatus.Hidden);
          window.location.reload();
        } else {
          LogService.info(`User cancelled refreshing during card payment`);
        }
      }
    }

    return {
      t,
      refreshWarning,
    };
  },
  async mounted() {
    // Skip all main window triggers/listeners for customer display
    if (isCustomerDisplayWindow()) {
      return;
    }

    LogService.info(`Scanner: going to attach scanner to the App vue`);
    attachedVueElement();
    (window as any).resetGrowingCollections = resetGrowingCollections;
    (window as any).resetGrowingCollectionsFromUI =
      resetGrowingCollecitonsFromUI;
    this.triggerSynchingIssuesMonitor();
    this.triggerAppPerformanceMonitoring();
    if (ReceiptModule.receiptCounter) {
      this.triggerReceiptCountersMonitoring();
    }
    loadStonly();
    loadStatusPage();
    window.addEventListener("beforeunload", this.refreshWarning);
    this.triggerLogsSync();
    this.triggerCashPointClosureSync();
    this.triggerAccessTokenCheck();
    this.checkEfrConnection();
    try {
      loadOffers();
      LogService.info("Offers object instantiated");
    } catch (error) {
      LogService.info("Failed to instantiate offers object", { error });
    }
    try {
      await initializeComplianceLib();
    } catch (error) {
      LogService.info("Failed to initialize compliance lib", { error });
    }
  },
  methods: {
    triggerSynchingIssuesMonitor() {
      const periodMilliSeconds = 1000 * 60 * 5;
      setInterval(() => {
        logPendingIgnoredAndFailedBookings();
      }, periodMilliSeconds);
    },
    triggerReceiptCountersMonitoring() {
      const periodMilliSeconds = 1000 * 60 * 5;
      setInterval(() => monitorReceiptSynchingIssues(), periodMilliSeconds);
    },
    triggerAppPerformanceMonitoring() {
      // Each 15 minutes we monitor how is the app doing
      const periodMilliSeconds = 1000 * 60 * 10;
      setInterval(() => monitorAppPerformance(), periodMilliSeconds);
    },
    triggerLogsSync() {
      const periodMilliSeconds = 1000 * 5;
      setInterval(() => syncLogs(), periodMilliSeconds);
    },
    triggerCashPointClosureSync() {
      const periodMilliSeconds = 1000 * 30;
      setInterval(() => cashPointClosuresSynchHandler(), periodMilliSeconds);
    },
    triggerAccessTokenCheck() {
      const periodMilliSeconds = 30 * 60 * 1000;
      setInterval(() => checkUserAccessToken(), periodMilliSeconds);
    },
    checkEfrConnection() {
      // check the fiscal device connection every 5 seconds
      const periodMilliSeconds = 5 * 1000;
      setInterval(async () => {
        if (isBelgianCompliant.value) {
          // Belgium talks to the FDM rather than a local EFR server
          await testFdmConnection();
          return;
        }
        if (!isSpanishCompliant.value && !isFrenchCompliant.value) {
          // applies only to Spain & France
          return;
        }
        await testEfrConnection();
      }, periodMilliSeconds);
    },
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.refreshWarning);
  },
});
</script>
